import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { fetchUserPayments } from '../../redux/paymentSlice';
import DashboardLayout from '../../components/layout/DashboardLayout';
import UserSidebar from '../../components/sidebar/UserSidebar';
import StatusBadge from '../../components/StatusBadge';
import { Link } from 'react-router-dom';
import './MyPaymentsPage.css';

const MyPaymentsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { payments, loading } = useSelector((state: RootState) => state.payment);

  useEffect(() => {
    dispatch(fetchUserPayments());
  }, [dispatch]);

  const formatCurrency = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <DashboardLayout sidebar={<UserSidebar />}>
      <div className="my-payments-page">
        <div className="page-header">
          <h2>My Payment Requests</h2>
          <p>Track the status of your international payment requests</p>
        </div>

        <div className="payments-container">
          {loading ? (
            <div className="loading">
              <p>Loading your payments...</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="no-payments">
              <p>You haven't submitted any payment requests yet.</p>
              <Link to="/dashboard/payments" className="submit-payment-btn">
                Submit New Payment Request
              </Link>
            </div>
          ) : (
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Recipient</th>
                  <th>Amount</th>
                  <th>Local Amount</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment._id}>
                    <td className="recipient-info">
                      <div>{payment.recipientCompany}</div>
                      <div className="account-number">{payment.accountNumber}</div>
                    </td>
                    <td className="foreign-amount">
                      {formatCurrency(payment.foreignAmount, payment.foreignCurrency)}
                    </td>
                    <td className="local-amount">
                      {formatCurrency(payment.localAmount, 'NGN')}
                    </td>
                    <td>
                      <StatusBadge status={payment.status} type="payment" />
                    </td>
                    <td className="submitted-date">
                      {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="actions">
                      <div className="action-stack">
                        <Link
                          to={`/dashboard/payment-details/${payment._id}`}
                          className="view-details-btn"
                        >
                          View Details
                        </Link>
                        {payment.status === 'rejected' && payment.rejectionReason && (
                          <div className="feedback-text">Feedback: {payment.rejectionReason}</div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyPaymentsPage;
